<script lang="ts" setup>
import { throttle } from 'lodash/fp';
import { computed, ref, watch, type PropType } from 'vue';
import { useRouter } from 'vue-router';

import { authService } from '@exchange/libs/auth/service/src';
import { balanceService, filterBySearchAndSortByPortfolioSize } from '@exchange/libs/balances/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { marketService } from '@exchange/libs/market/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import BalancesListExchangeDesktop from './BalancesListExchangeDesktop.vue';
import BalancesListExchangeMobile from './BalancesListExchangeMobile.vue';

defineProps({
  authenticatedUser: { type: Boolean, required: true },
  historySource: { type: Object as PropType<HistoryDataSource>, required: true },
});

const { isXS } = useMQBreakpoints();
const router = useRouter();

const search = ref<string>();
const { balanceSnapshotReceived } = balanceService;
const { getCurrencyName } = currencyService;
const { marketDataLoading } = marketService;

const balanceSource = computed(() => balanceService.list.value);

const getSortedBalanceList = () => filterBySearchAndSortByPortfolioSize(balanceSource.value, search.value, getCurrencyName);

const sortedList = ref(getSortedBalanceList());
const updateDebouncedSortedList = () => {
  sortedList.value = getSortedBalanceList();
};

watch(balanceSource, throttle(5_000, updateDebouncedSortedList));

const balanceLoadingUntil = computed(() => {
  if (!authService.hasBeenAuthenticated) {
    return false;
  }

  return !balanceSnapshotReceived.value;
});

watch([balanceLoadingUntil, marketDataLoading], ([bl, md]) => {
  if (!bl || !md) {
    updateDebouncedSortedList();
  }
});

const handleDeposit = (currencyCode?: string) => {
  router.push({ hash: '#Deposit', query: { currency: currencyCode } });
};

const handleTransfer = (currencyCode: string) => {
  const query = {
    currency: currencyCode,
    destination: CONSTANTS.INSTANT_TRADE_ACCOUNT_NAME,
    source: CONSTANTS.MAIN_SUBACCOUNT_NAME,
  };
  router.push({ hash: '#Transfer', query });
};
</script>

<template>
  <div class="balances-list-exchange-wrapper">
    <balances-list-exchange-desktop
      v-if="!isXS"
      :authenticated-user="authenticatedUser"
      :history-source="historySource"
      :sorted-list="sortedList"
      @deposit="handleDeposit"
      @transfer="handleTransfer"
    />
    <balances-list-exchange-mobile
      v-else
      :authenticated-user="authenticatedUser"
      :history-source="historySource"
      :sorted-list="sortedList"
      @deposit="handleDeposit"
      @transfer="handleTransfer"
    />
  </div>
</template>
